import { useCallback, useEffect, useMemo, useState } from "react";
import { matchPath, Outlet, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { api, type AuthStatus, type ProductWithDemos } from "../api";
import { IconAlert, IconBolt, IconCapture, IconChart, IconDownload, IconEye, IconFolder, IconSettings, IconGrid, IconLayers, IconMonitor, IconMoon, IconPlus, IconSignOut, IconSun, IconUser } from "../icons";
import { monitoringOn, reportError } from "../monitoring";
import { signInPath, useAdminAuth } from "./auth/AuthContext";
import AuthScreen, { AuthLoading } from "./auth/AuthScreen";
import { confirmLeave } from "./dirty";
import CommandPalette, { type PaletteItem } from "./shell/CommandPalette";
import TopBar, { type Where } from "./shell/TopBar";

export interface ShellContext {
  products: ProductWithDemos[];
  refreshNav: () => Promise<void>;
}
export const useShell = () => useOutletContext<ShellContext>();

const PAGE_NAMES: [string, string][] = [
  ["/admin/captures", "Captures"],
  ["/admin/new", "New product"],
];

export default function AdminShell() {
  const auth = useAdminAuth();
  const [ready, setReady] = useState(false);
  const [email, setEmail] = useState<string | undefined>();
  /** Signed in, but the server won't let them in (or couldn't check): say why rather than bounce to sign-in. */
  const [refused, setRefused] = useState<AuthStatus | null>(null);
  const [unreachable, setUnreachable] = useState(false);
  const [palette, setPalette] = useState(false);
  const [products, setProducts] = useState<ProductWithDemos[]>([]);
  const nav = useNavigate();
  const { pathname } = useLocation();

  const refreshNav = useCallback(async () => {
    setProducts(await api.products());
  }, []);

  useEffect(() => {
    document.title = "DemoDive admin";
    setReady(false); setRefused(null); setUnreachable(false);
    if (auth.mode === "unconfigured" || (auth.mode === "clerk" && !auth.signedIn)) {
      nav(signInPath(), { replace: true });
      return;
    }
    let live = true;
    api.me().then(async (me) => {
      if (!live) return;
      if (me.allowed) {
        setEmail(me.email);
        await refreshNav();
        if (live) setReady(true);
      } else if (auth.mode === "local" && !me.authenticated) {
        nav(signInPath(), { replace: true });
      } else {
        setRefused(me);
      }
    }).catch(() => { if (live) setUnreachable(true); });
    return () => { live = false; };
  }, [auth.mode, auth.signedIn, nav, refreshNav]);

  const [theme, setThemeState] = useState<string>(() => document.documentElement.dataset.theme ?? "system");
  const setTheme = useCallback((t: string) => {
    setThemeState(t);
    if (t === "system") delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = t;
    try { t === "system" ? localStorage.removeItem("theme") : localStorage.setItem("theme", t); } catch { /* storage blocked */ }
  }, []);

  const logout = useCallback(async () => {
    await auth.signOut();
    nav("/admin/login", { replace: true });
  }, [auth, nav]);

  /** Sends one deliberate error from this page and one from the API, to check that both reach Sentry. */
  const sendTestError = useCallback(async () => {
    if (!monitoringOn()) { alert("Error monitoring is off in this environment: set SENTRY_DSN to turn it on."); return; }
    reportError(new Error("DemoDive monitoring test: the admin app sent this on purpose."));
    await api.monitoringTest().catch(() => { /* a 500 is the point */ });
    alert("Sent two test errors, one from this page and one from the API. They show up in Sentry within a minute or so.");
  }, []);

  /** Every navigation from the shell asks first if the open screen has unsaved edits. */
  const go = useCallback((to: string) => { if (confirmLeave()) { nav(to); window.scrollTo(0, 0); } }, [nav]);

  // Cmd/Ctrl+K opens the command menu from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPalette((o) => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const where: Where = useMemo(() => {
    const d = matchPath("/admin/demos/:demoId/:tab", pathname) ?? matchPath("/admin/demos/:demoId", pathname);
    if (d) {
      for (const p of products) {
        const demo = p.demos.find((x) => x.id === d.params.demoId);
        if (demo) return { kind: "demo", product: p, demo, tab: (d.params as { tab?: string }).tab ?? "basics" };
      }
    }
    const pr = matchPath("/admin/products/:productId", pathname);
    if (pr) return { kind: "page", page: products.find((p) => p.id === pr.params.productId)?.name ?? "Product" };
    return { kind: "page", page: PAGE_NAMES.find(([p]) => pathname.startsWith(p))?.[1] ?? "Demos" };
  }, [pathname, products]);

  const items: PaletteItem[] = useMemo(() => {
    const out: PaletteItem[] = [];
    if (where.kind === "demo" && where.demo) {
      const base = `/admin/demos/${where.demo.id}`;
      const tab = (key: string, label: string, icon: React.ReactNode) =>
        out.push({ id: `tab-${key}`, group: `This demo: ${where.demo!.title}`, label, icon, run: () => go(`${base}/${key}`) });
      tab("basics", "Settings / Basics", <IconSettings />);
      tab("outcomes", "Paths", <IconGrid />);
      tab("studio", "Studio", <IconLayers />);
      tab("survey", "Survey", <IconGrid />);
      tab("preview", "Preview", <IconEye />);
      tab("analytics", "Analytics", <IconChart />);
      tab("publish", "Publish & share", <IconBolt />);
    }
    out.push({ id: "go-demos", group: "Go to", label: "All demos", icon: <IconGrid />, run: () => go("/admin") });
    out.push({ id: "go-captures", group: "Go to", label: "Captures", icon: <IconCapture />, run: () => go("/admin/captures") });
    out.push({ id: "go-new", group: "Go to", label: "New product", icon: <IconPlus />, run: () => go("/admin/new") });
    for (const p of products) {
      out.push({ id: `p-${p.id}`, group: "Products", label: p.name, hint: "Product settings", icon: <IconFolder />, run: () => go(`/admin/products/${p.id}`) });
      for (const d of p.demos) {
        out.push({ id: `d-${d.id}`, group: "Demos", label: d.title, hint: p.name, icon: <IconLayers />, run: () => go(`/admin/demos/${d.id}/studio`) });
      }
    }
    out.push({ id: "theme-light", group: "Preferences", label: "Theme: Light", icon: <IconSun />, run: () => setTheme("light") });
    out.push({ id: "theme-dark", group: "Preferences", label: "Theme: Dark", icon: <IconMoon />, run: () => setTheme("dark") });
    out.push({ id: "theme-system", group: "Preferences", label: "Theme: Match system", icon: <IconMonitor />, run: () => setTheme("system") });
    out.push({ id: "export", group: "Account", label: "Export all data (JSON)", icon: <IconDownload />, run: () => { api.downloadExport().catch((e) => alert(e.message)); } });
    if (auth.manageAccount) out.push({ id: "account", group: "Account", label: "Manage account", hint: email, icon: <IconUser />, run: auth.manageAccount });
    out.push({ id: "monitoring-test", group: "Account", label: "Send a test error to monitoring", icon: <IconAlert />, run: () => { void sendTestError(); } });
    out.push({ id: "signout", group: "Account", label: "Sign out", icon: <IconSignOut />, run: () => { void logout(); } });
    return out;
  }, [where, products, go, setTheme, logout, auth.manageAccount, email, sendTestError]);

  if (refused) {
    const title = refused.code === "invalid_session" || refused.code === "signed_out" ? "We couldn't verify your sign-in"
      : refused.code === "missing_claims" ? "One more Clerk setting"
        : refused.code === "email_unverified" ? "Verify your email address"
          : "No access to this workspace";
    return <AuthScreen title={title} email={refused.email} message={refused.message + (refused.detail ? ` (${refused.detail})` : "")}
      actions={<button type="button" className="btn btn-primary" onClick={() => void logout()}>Sign out</button>} />;
  }
  if (unreachable) {
    return <AuthScreen title="Can't reach DemoDive" message="The server didn't answer. Check your connection and reload."
      actions={<button type="button" className="btn btn-primary" onClick={() => location.reload()}>Reload</button>} />;
  }
  if (!ready) return <AuthLoading />;

  // The Studio wants the whole window; every other page sits in a centred column.
  const bleed = where.kind === "demo" && where.tab === "studio";

  return (
    <div className={`admin ${bleed ? "bleed" : ""}`}>
      <TopBar where={where} products={products} go={go} onSearch={() => setPalette(true)} theme={theme} setTheme={setTheme} onLogout={logout} email={email} onManageAccount={auth.manageAccount} />
      <main className="main">
        <Outlet context={{ products, refreshNav } satisfies ShellContext} />
      </main>
      <CommandPalette open={palette} onClose={() => setPalette(false)} items={items} />
    </div>
  );
}
